/**
 * Hook to manage notification onboarding modal state.
 *
 * Determines when to show the modal based on:
 * - User has created tasks (taskCount threshold)
 * - setup_completed is false
 * - setup_prompt_count controls modal vs persistent reminder
 *
 * Logic:
 * - prompt_count = 0 and tasks >= 1 -> show modal
 * - prompt_count = 1 and tasks >= 5 -> show modal
 * - prompt_count >= 2 -> no modal, show persistent reminder + badge
 * - setup_completed = true -> nothing shown
 */
import { useState, useCallback, useEffect } from 'react';
import { useNotificationPreferences } from './useNotificationPreferences';

interface UseNotificationOnboardingOptions {
  /** Total number of task definitions the user has created */
  taskCount: number;
}

interface UseNotificationOnboardingResult {
  /** Whether the onboarding modal should be displayed */
  modalOpen: boolean;
  /** Open the onboarding modal manually */
  openModal: () => void;
  /** Close the onboarding modal */
  closeModal: () => void;
  /** Whether to show a persistent reminder link on the Tasks page */
  shouldShowReminder: boolean;
  /** Whether to show a badge indicator on the Settings nav item */
  shouldShowBadge: boolean;
  /** Whether preferences are still loading */
  isLoading: boolean;
}

export function useNotificationOnboarding({
  taskCount,
}: UseNotificationOnboardingOptions): UseNotificationOnboardingResult {
  const { data: preferences, isLoading } = useNotificationPreferences();
  const [modalOpen, setModalOpen] = useState(false);
  const [hasTriggered, setHasTriggered] = useState(false);

  // Determine if we should show the modal
  const shouldShowModal = useCallback((): boolean => {
    if (isLoading || !preferences) return false;
    if (preferences.setup_completed) return false;
    if (preferences.enabled) return false;

    const promptCount = preferences.setup_prompt_count;

    // First task created: show modal (if not yet prompted)
    if (taskCount >= 1 && promptCount === 0) return true;

    // 5th task: show again if dismissed once
    if (taskCount >= 5 && promptCount === 1) return true;

    return false;
  }, [isLoading, preferences, taskCount]);

  // Auto-trigger modal when conditions are met
  useEffect(() => {
    if (shouldShowModal() && !hasTriggered) {
      setModalOpen(true);
      setHasTriggered(true);
    }
  }, [shouldShowModal, hasTriggered]);

  const openModal = useCallback(() => {
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  // Show persistent reminder when user dismissed twice but hasn't completed setup
  const shouldShowReminder =
    !isLoading &&
    !!preferences &&
    !preferences.setup_completed &&
    !preferences.enabled &&
    preferences.setup_prompt_count >= 2;

  // Show badge when setup is not completed and notifications are not enabled
  const shouldShowBadge =
    !isLoading &&
    !!preferences &&
    !preferences.setup_completed &&
    !preferences.enabled;

  return {
    modalOpen,
    openModal,
    closeModal,
    shouldShowReminder,
    shouldShowBadge,
    isLoading,
  };
}
